import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';

function getGrade(mark) {
  if (mark >= 50) return 'AA';
  if (mark >= 48) return 'A+';
  if (mark >= 45) return 'A';
  if (mark >= 43) return 'B+';
  if (mark >= 40) return 'B';
  if (mark >= 35) return 'C+';
  if (mark >= 30) return 'C';
  if (mark >= 25) return 'D';
  if (mark >= 20) return 'E';
  return 'F';
}

function formatTime(utcStr) {
  if (!utcStr) return 'N/A';
  const isoStr = utcStr.endsWith('Z') ? utcStr : utcStr + 'Z';
  const localDate = new Date(isoStr);
  const localTimeStr = localDate.toLocaleString(undefined, { 
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
  
  // Get short timezone name (e.g. GST, IST)
  let tzName = '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(localDate);
    tzName = parts.find(p => p.type === 'timeZoneName')?.value || '';
  } catch(e) {}
  
  return `${localTimeStr} ${tzName ? `(${tzName})` : ''}`;
}

export default function ResultView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showQuestions, setShowQuestions] = useState(false);

  useEffect(() => {
    const fetchResult = async () => {
      try {
        const response = await api.get(`/exam/result/${id}`);
        setData(response.data);
      } catch (err) {
        if (err.response?.status === 401) {
          navigate('/exam');
        } else if (err.response?.status === 403) {
          setError('Results for this exam are currently locked.');
        } else {
          setError('Failed to load result. ' + (err.response?.data?.error || ''));
        }
      } finally {
        setLoading(false);
      }
    };
    fetchResult();
  }, [id, navigate]);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingState}>Loading your result...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorState}>
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => {
            const isTest = new URLSearchParams(window.location.search).get('testChannel') === 'true';
            navigate(isTest ? '/student-react/test-channel' : '/student-react/dashboard');
          }} style={styles.backBtn}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { student, exam_info, total_marks, questions } = data;
  const grade = getGrade(total_marks);

  let start_time = exam_info?.start_time;
  let end_time = exam_info?.end_time;
  let display_duration = exam_info?.duration || 30;

  if (start_time && end_time) {
    const startIso = start_time.endsWith('Z') ? start_time : start_time + 'Z';
    const endIso = end_time.endsWith('Z') ? end_time : end_time + 'Z';
    
    const startDate = new Date(startIso);
    let endDate = new Date(endIso);
    
    // Cap time: 15:35 Oman Time = 11:35 UTC on the same day as start
    const capDate = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate(), 11, 35, 0));
    
    if (endDate > capDate) {
      endDate = capDate;
      end_time = endDate.toISOString();
    }
    
    // Recalculate duration in minutes
    const durationMs = endDate - startDate;
    if (durationMs > 0) {
      display_duration = Math.floor(durationMs / 60000);
    }
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button onClick={() => {
          const isTest = new URLSearchParams(window.location.search).get('testChannel') === 'true';
          navigate(isTest ? '/student-react/test-channel' : '/student-react/dashboard');
        }} style={styles.backBtn}>&larr; Dashboard</button>
        <h1 style={styles.title}>Exam Result</h1>
      </div>

      <div style={styles.content}>
        {/* Unified Result Table Card */}
        <div style={styles.card}>
          <table style={styles.resultTable}>
            <tbody>
              <tr>
                <td style={styles.tableLabel}>Name</td>
                <td style={styles.tableValue}>{student.name}</td>
              </tr>
              <tr>
                <td style={styles.tableLabel}>Classroom</td>
                <td style={styles.tableValue}>{student.classroom}</td>
              </tr>
              <tr>
                <td style={styles.tableLabel}>Nation</td>
                <td style={styles.tableValue}>{student.nation}</td>
              </tr>
              <tr>
                <td style={styles.tableLabel}>Exam Name</td>
                <td style={styles.tableValue}>{exam_info?.exam_name || 'HADIYA EXAM'}</td>
              </tr>
              <tr>
                <td style={styles.tableLabel}>Total Mark</td>
                <td style={styles.tableValue}>50</td>
              </tr>
              <tr>
                <td style={styles.tableLabel}>Student Mark</td>
                <td style={{ ...styles.tableValue, fontWeight: '900', fontSize: '20px', color: '#3182ce' }}>{total_marks}</td>
              </tr>
              <tr>
                <td style={styles.tableLabel}>Grade</td>
                <td style={{ ...styles.tableValue, fontWeight: '900', fontSize: '20px', color: '#48bb78' }}>{grade}</td>
              </tr>
              <tr>
                <td style={styles.tableLabel}>Exam Started Time</td>
                <td style={styles.tableValue}>{formatTime(start_time)}</td>
              </tr>
              <tr>
                <td style={styles.tableLabel}>End Time</td>
                <td style={styles.tableValue}>{formatTime(end_time)}</td>
              </tr>
              <tr>
                <td style={styles.tableLabel}>Exam Duration</td>
                <td style={styles.tableValue}>{display_duration} mins</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Questions Breakdown Accordion */}
        <div style={styles.card}>
          <button 
            style={styles.accordionBtn} 
            onClick={() => setShowQuestions(!showQuestions)}
          >
            <span style={styles.accordionTitle}>Detailed Question Breakdown</span>
            <span style={{ transform: showQuestions ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}>
              ▼
            </span>
          </button>

          {showQuestions && (
            <div style={styles.questionsList}>
              {questions.map((q, index) => {
                const isCorrect = q.student_mark > 0;
                const studentAnswer = q.student_answer || 'Not Answered';
                return (
                  <div key={q.question_id} style={{
                    ...styles.questionItem,
                    borderLeft: `4px solid ${isCorrect ? '#48bb78' : '#e53e3e'}`
                  }}>
                    <p style={styles.questionText}>
                      <strong>Q{index + 1}.</strong> {q.question}
                      {q.question_l1 && <br />}
                      {q.question_l1 && <span style={{color: '#4a5568', fontSize: '15px'}}>{q.question_l1}</span>}
                    </p>
                    <div style={styles.answerGrid}>
                      <div style={styles.answerBlock}>
                        <span style={styles.answerLabel}>Right Answer:</span>
                        <span style={{ color: '#48bb78', fontWeight: '500' }}>
                          {q.correct_answer}
                          {q.correct_answer_l1 && <span style={{display: 'block', fontSize: '13px', color: '#718096', marginTop: '2px'}}>{q.correct_answer_l1}</span>}
                        </span>
                      </div>
                      <div style={styles.answerBlock}>
                        <span style={styles.answerLabel}>Your Answer:</span>
                        <span style={{ color: isCorrect ? '#48bb78' : '#e53e3e', fontWeight: '500' }}>
                          {studentAnswer}
                          {q.student_answer_l1 && studentAnswer !== 'Not Answered' && <span style={{display: 'block', fontSize: '13px', color: '#718096', marginTop: '2px'}}>{q.student_answer_l1}</span>}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #e4eaf5 100%)',
    padding: '40px 20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '40px',
    maxWidth: '800px',
    margin: '0 auto 40px auto',
  },
  backBtn: {
    padding: '10px 20px',
    background: '#ffffff',
    color: '#4a5568',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    cursor: 'pointer',
    marginRight: '20px',
    fontWeight: '600',
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
    transition: 'all 0.2s',
  },
  title: {
    color: '#1a202c',
    margin: 0,
    fontSize: '28px',
    fontWeight: '800',
    letterSpacing: '-0.5px',
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  card: {
    background: '#ffffff',
    borderRadius: '20px',
    padding: '30px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.04)',
    border: '1px solid rgba(255,255,255,0.5)',
  },
  resultTable: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableLabel: {
    padding: '12px 16px',
    color: '#718096',
    fontSize: '14px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontWeight: '600',
    borderBottom: '1px solid #edf2f7',
    width: '35%',
    verticalAlign: 'middle',
  },
  tableValue: {
    padding: '12px 16px',
    color: '#2d3748',
    fontSize: '16px',
    fontWeight: '700',
    borderBottom: '1px solid #edf2f7',
    verticalAlign: 'middle',
  },
  accordionBtn: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'none',
    border: 'none',
    color: '#2d3748',
    fontSize: '18px',
    fontWeight: '700',
    cursor: 'pointer',
    padding: '10px 0',
  },
  accordionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  questionsList: {
    marginTop: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  questionItem: {
    background: '#f8fafc',
    padding: '20px',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
  },
  questionText: {
    color: '#2d3748',
    margin: '0 0 16px 0',
    fontSize: '16px',
    lineHeight: '1.6',
    fontWeight: '500',
  },
  answerGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    background: '#ffffff',
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid #edf2f7',
  },
  answerBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  answerLabel: {
    color: '#718096',
    fontSize: '12px',
    textTransform: 'uppercase',
    fontWeight: '600',
    letterSpacing: '0.5px',
  },
  loadingState: {
    color: '#2d3748',
    textAlign: 'center',
    marginTop: '50px',
    fontSize: '20px',
    fontWeight: '600',
  },
  errorState: {
    background: '#fff5f5',
    color: '#c53030',
    padding: '40px',
    borderRadius: '16px',
    textAlign: 'center',
    maxWidth: '450px',
    margin: '50px auto',
    border: '1px solid #feb2b2',
    boxShadow: '0 10px 25px rgba(197, 48, 48, 0.1)',
  }
};
